/**
 * @Description 组件提交用数据
 */

import { floorKeep, isNotEmpty, isNotEmptyAny } from '@lhb/func';
import {
  CoreInfoRelateNames,
  isCollaborationModeDiscount,
  isCollaborationModeHighValue,
} from '../ts-config';


export const transferSubmitData = (
  obj: any,
  form: any,
  collaborationModeOptions: any[]
) => {
  if (!isNotEmptyAny(obj)) return null;

  const {
    saleTarget,
    operatingIncome,
    costExpense,
    employeeCompensationCost,
    storeImageCost,
    storeEngineeringCost,
    leaseCost,
    otherCost,
  } = obj;

  const saleTargetVal = {
    name: '销售指标',
    yearData: [
      saleTarget?.firstYear || {},
      saleTarget?.secondYear || {},
    ],
  };

  const operatingIncomeVal = {
    name: '营业收入',
    yearData: [
      operatingIncome?.firstYear || {},
      operatingIncome?.secondYear || {},
    ],
  };

  const costExpenseVal = {
    name: '费用成本',
    yearData: [
      costExpense?.firstYear || {},
      costExpense?.secondYear || {},
    ],
  };

  const employeeCompensationCostVal = {
    name: '职工薪酬成本',
    yearData: [
      employeeCompensationCost?.firstYear || {},
      employeeCompensationCost?.secondYear || {},
    ],
  };

  const storeImageCostVal = {
    name: '店铺形象成本',
    data: storeImageCost || {},
  };

  const storeEngineeringCostVal = {
    name: '店铺工程成本',
    data: storeEngineeringCost || {},
  };

  const leaseCostVal = {
    name: '租赁费用成本',
    yearData: [
      leaseCost?.firstYear || {},
      leaseCost?.secondYear || {},
    ],
  };

  const otherCostVal = {
    name: '其他类别费用成本',
    yearData: [
      otherCost?.firstYear || {},
      otherCost?.secondYear || {},
    ],
  };

  const coreInfoModuleVal = {
    name: '核心信息',
    yearData: calcCoreInfos(obj, form, collaborationModeOptions),
  };

  const res = {
    saleTarget: saleTargetVal,
    operatingIncome: operatingIncomeVal,
    costExpense: costExpenseVal,
    employeeCompensationCost: employeeCompensationCostVal,
    storeImageCost: storeImageCostVal,
    storeEngineeringCost: storeEngineeringCostVal,
    leaseCost: leaseCostVal,
    otherCost: otherCostVal,
    coreInfoModule: coreInfoModuleVal,
  };
  // console.log('transferSubmitData obj:', obj, 'res:', res);
  return res;
};

const calcCoreInfos = (obj: any, form: any, collaborationModeOptions: any[]) => {
  const {
    saleTarget,
    operatingIncome,
    costExpense,
    employeeCompensationCost,
    leaseCost,
  } = obj;

  /*
    合作模式，影响 租售比
    合同面积，影响 年坪效、月坪效
    员工编制，影响 人效
  */
  const collaborationMode = form.getFieldValue(CoreInfoRelateNames.collaborationMode);
  const basicUsableArea = form.getFieldValue(CoreInfoRelateNames.basicUsableArea);
  const employeeStaffing = form.getFieldValue(CoreInfoRelateNames.employeeStaffing);

  const saleTargetFirst = saleTarget?.firstYear || {};
  const saleTargetSecond = saleTarget?.secondYear || {};

  const operatingIncomeFirst = operatingIncome?.firstYear || {};
  const operatingIncomeSecond = operatingIncome?.secondYear || {};

  const costExpenseFirst = costExpense?.firstYear || {};
  const costExpenseSecond = costExpense?.secondYear || {};

  const employeeCompensationCostFirst = employeeCompensationCost?.firstYear || {};
  const employeeCompensationCostSecond = employeeCompensationCost?.secondYear || {};

  const leaseCostFirst = leaseCost?.firstYear || {};
  const leaseCostSecond = leaseCost?.secondYear || {};

  const firstYear = coreInfoOneYear({
    collaborationMode,
    collaborationModeOptions,
    basicUsableArea,
    employeeStaffing,
    saleTarget: saleTargetFirst,
    operatingIncome: operatingIncomeFirst,
    costExpense: costExpenseFirst,
    employeeCompensationCost: employeeCompensationCostFirst,
    leaseCost: leaseCostFirst
  });

  const secondYear = coreInfoOneYear({
    collaborationMode,
    collaborationModeOptions,
    basicUsableArea,
    employeeStaffing,
    saleTarget: saleTargetSecond,
    operatingIncome: operatingIncomeSecond,
    costExpense: costExpenseSecond,
    employeeCompensationCost: employeeCompensationCostSecond,
    leaseCost: leaseCostSecond
  });

  return [
    firstYear,
    secondYear,
  ];
};

const coreInfoOneYear = ({
  collaborationMode,
  collaborationModeOptions,
  basicUsableArea,
  employeeStaffing,
  saleTarget,
  operatingIncome,
  costExpense,
  employeeCompensationCost,
  leaseCost,
}) => {

  // 终端销售指标
  const saleTargetVal = saleTarget?.saleTarget;
  // 营业收入
  const operatingIncomeVal = operatingIncome?.operatingIncome;
  // 租赁成本
  const leaseCostVal = leaseCost?.leaseCost;
  // 租金
  const rent = leaseCost?.rent;
  // 物业管理费
  const propertyManagementFee = leaseCost?.propertyManagementFee;
  // 商场联营扣点
  const discountRate = leaseCost?.discountRate;
  // 职工薪酬费用
  const employeeSalary = employeeCompensationCost?.employeeSalary;

  // 1.利润总额：利润总额/10000，单位：万，保存小数点后两位
  const totalProfit = isNotEmpty(costExpense?.totalProfit) ? fixPrecision2Param(+costExpense?.totalProfit / 10000.0) : null;

  // 2.利润率：引用定制组件中「利润额」的值
  const profitMargin = costExpense?.profitMargin;

  // 3.毛利率：引用定制组件中「财务毛利率」的值
  const grossMargin = operatingIncome?.financialGrossProfitMargin;

  /*
    4.租售比
    租赁：租赁成本/终端销售指标
    扣点：（终端销售指标*商场联营扣点+物业管理费）/终端销售指标
    取高：取两者高值
  */
  const priceToRent = calPriceToRent({
    collaborationMode,
    collaborationModeOptions,
    rent,
    propertyManagementFee,
    saleTargetVal,
    discountRate,
  });

  // 5.租赁费用率：租赁成本/营业收入
  const rentalRatio = isNotEmpty(leaseCostVal) && operatingIncomeVal ? fixPrecisionParam((+leaseCostVal) * 100.0 / +operatingIncomeVal) : null;

  // 6.薪酬费用率：职工薪酬费用/营业收入
  const salaryRatio = isNotEmpty(employeeSalary) && operatingIncomeVal ? fixPrecisionParam((+employeeSalary) * 100.0 / +operatingIncomeVal) : null;

  // 7.人效：终端销售指标/员工编制数量/10000，单位：万/人
  const hrEfficiency = isNotEmpty(saleTargetVal) && employeeStaffing ? fixPrecisionParam((+saleTargetVal) / (+employeeStaffing) / 10000) : null;

  // 8.年坪效(规划面积)：终端销售指标/提报合同面积/10000
  const yearSalesEffect: any = isNotEmpty(saleTargetVal) && basicUsableArea ? fixPrecisionParam((+saleTargetVal) / (+basicUsableArea) / 10000) : null;

  // 9.月坪效(规划面积)：终端销售指标/提报合同面积/10000/12
  const monthSalesEffect = isNotEmpty(saleTargetVal) && basicUsableArea ? fixPrecisionParam((+saleTargetVal) / (+basicUsableArea) / 12 / 10000) : null;

  return {
    totalProfit,
    profitMargin,
    grossMargin,
    priceToRent,
    rentalRatio,
    salaryRatio,
    hrEfficiency,
    yearSalesEffect,
    monthSalesEffect,
  };
};

/**
 * @description
 *
    租售比
    租赁：租赁成本/终端销售指标
    扣点：（终端销售指标*商场联营扣点+物业管理费）/终端销售指标
    取高：取两者高值
 */
const calPriceToRent = ({
  collaborationMode,
  collaborationModeOptions,
  rent,
  saleTargetVal,
  propertyManagementFee,
  discountRate,
}) => {
  let val: any = null;

  const selOpt = collaborationModeOptions.find((itm: any) => itm.id === collaborationMode);
  if (isCollaborationModeDiscount(selOpt?.name)) { // 扣点
    val = calcModeDiscount({ discountRate, propertyManagementFee, saleTargetVal });
  } else if (isCollaborationModeHighValue(selOpt?.name)) { // 取高
    val = calcModeHighValue({ rent, propertyManagementFee, saleTargetVal, discountRate });
  } else { // 选择租赁、及未选时，都当作选中租赁
    val = calcModeRent({ rent, propertyManagementFee, saleTargetVal });
  }
  return val;
};

/**
 * @description     租售比（租赁模式）
      租赁：(租金+物业管理费)/终端销售指标
  * @param rent 租金
  * @param propertyManagementFee 物业管理费
  * @param saleTargetVal 终端销售指标
  * @return
  */
const calcModeRent = ({ rent, propertyManagementFee, saleTargetVal }) => {
  let valFee: any = null; // 租金+物业管理费
  isNotEmpty(rent) && (valFee += (+rent));
  isNotEmpty(propertyManagementFee) && (valFee += (+propertyManagementFee));
  isNotEmpty(valFee) && (valFee = fixIntParam(valFee));

  return isNotEmpty(rent) && saleTargetVal ? fixPrecisionParam(+valFee * 100 / +saleTargetVal) : null;
};

/**
 * @description     租售比（扣点模式）
      扣点：（终端销售指标*商场联营扣点+物业管理费）/终端销售指标
  * @param discountRate 商场联营扣点
  * @param propertyManagementFee 物业管理费
  * @param saleTargetVal 终端销售指标
  * @return
  */
const calcModeDiscount = ({ discountRate, propertyManagementFee, saleTargetVal }) => {
  return isNotEmpty(discountRate) && isNotEmpty(propertyManagementFee) && saleTargetVal ? fixPrecisionParam(((+saleTargetVal) * (+discountRate / 100) + (+propertyManagementFee)) * 100 / +saleTargetVal) : null;
};

/**
 * @description     租售比（取高模式）
    租赁：(租金+物业管理费)/终端销售指标
    扣点：（终端销售指标*商场联营扣点+物业管理费）/终端销售指标
    取高：取两者高值
  * @param rent 租金
  * @param propertyManagementFee 物业管理费
  * @param saleTargetVal 终端销售指标
 * @param discountRate 商场联营扣点
 * @return
 */
const calcModeHighValue = ({ rent, propertyManagementFee, saleTargetVal, discountRate }) => {
  let val: any = null;
  const leaseVal: any = calcModeRent({ rent, propertyManagementFee, saleTargetVal });
  const discountVal: any = calcModeDiscount({ discountRate, propertyManagementFee, saleTargetVal });
  if (isNotEmpty(leaseVal) && isNotEmpty(discountVal)) {
    val = Math.max(leaseVal, discountVal);
  } else if (isNotEmpty(leaseVal)) {
    val = leaseVal;
  } else if (isNotEmpty(discountVal)) {
    val = discountVal;
  }
  return val;
};

/**
 * @description 取整
 * @param val 数据
 * @return 结果
 */
export const fixIntParam = (val: any) => {
  return floorKeep(val, 1, 3, 0);
};

/**
 * @description 取1位小数
 * @param val 数据
 * @return 结果
 */
export const fixPrecisionParam = (val: any) => {
  return floorKeep(val, 1, 3, 1);
};

/**
 * @description 取2位小数
 * @param val 数据
 * @return 结果
 */
export const fixPrecision2Param = (val: any) => {
  return floorKeep(val, 1, 3, 2);
};
